import { EventEmitter, Injectable } from '@angular/core';
import { InsparkMessageService } from '@inspark/inspark-components';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs/internal/observable/of';
import { environment } from '../../environments/environment';
import { ReactiveService } from '../../../projects/api/src/reactive-service.abstract';
import {IsAuthorizedService} from '../shared/services/is-authorized.service';
import {Confirmation, ConfirmationService} from 'primeng/api';


const SOCKET_EVENTS_URL = ((ws, location) => {
  const urlParts = location.match(/([^\:]*):\/\/([^\/]*)/);
  const protocolSuffix = urlParts[1] === 'https' ? 's' : '';
  const host = urlParts[2];

  return ws.substr(0, 2).toLowerCase() === 'ws' || ws.substr(0, 3).toLowerCase() === 'wss'
    ? `${ws}event`
    : `ws${protocolSuffix}://${host}${ws}event`;

})(environment.webSocket, document.location.href);

@Injectable()
export class NotificationService extends ReactiveService {

  audio = null;
  socket;
  connectionLost = { count: 0, lostDetected: false};
  public event$ = new EventEmitter();
  public event_cdp$ = new EventEmitter();
  public event_controller$ = new EventEmitter();
  public event_controller_cmd$ = new EventEmitter();

  constructor(private messageService: InsparkMessageService,
              private confirmationService: ConfirmationService,
              private translate: TranslateService,
              private isAuthService: IsAuthorizedService) {
    super();
    this.audio = new Audio();
    const event$$ = this.event$;
    this.audio.addEventListener('ended', function() {
      this.currentTime = 0;
      event$$.emit({action: 'playsound'});
      this.play();
    });
    this.linkWatcher();

  }

  linkWatcher(): void {
    let isOnline = window.navigator.onLine;
    console.log('Initially ' + (isOnline ? 'on' : 'off') + 'line, start watch');

    const options: Confirmation = {
      message: this.translate.instant('Connection lost'),
      header: this.translate.instant('Connection lost'),
      rejectVisible: true,
      acceptVisible: false,
      rejectLabel: this.translate.instant('Close'),
      reject: () => { console.log(isOnline); if (!isOnline) { setTimeout(() => showConfirmation(), 10); } }
    };

    const showConfirmation = () => {
      this.confirmationService.confirm(options);
    };

    if (!isOnline) {
      showConfirmation();
    }

    window.addEventListener('online',
      () => {
        isOnline = true;
        console.log('Became online');
      });

    window.addEventListener('offline',
      () => {
        isOnline = false;
        showConfirmation();
      }
    );
  }
  webSocket() {
    if (!this.socket && this.isAuthService.isAuthorized.getValue()) {
      this.socket = new WebSocket(SOCKET_EVENTS_URL);
      this.socket.onopen = () => {
        this.connectionLost = { count: 0, lostDetected: false};
        console.log('Open WEBSocket for events', SOCKET_EVENTS_URL);
      };
      this.socket.onclose = (...args) => {
        this.socket = null;
        this.webSocket();
        console.log('Socket closed', args);
      };
      this.socket.onmessage = (data) => this.parseWSData(JSON.parse(data.data));
      this.socket.onerror = (err) => {
        this.checkConnection(err);
        console.error({err});
      };
    }
  }

  checkConnection(err: Event): void {
    this.connectionLost.count++;

    if (this.connectionLost.count > 5) {
      this.connectionLost.lostDetected = true;
    }

    if (this.connectionLost.lostDetected) {
      this.messageService.error('Connection lost');
    }
  }
  parseWSData(jsonData) {
    if (jsonData.command) {
      switch (jsonData.command) {
        case 'READ_ALL_EVENT':
          this.sendEvent({name: 'custom', params: {type: 'read_all_event'}}, of({}));
          break;
        case 'READ_EVENT':
          this.sendEvent({name: 'custom', params: {type: 'read_event'}}, of({eid: jsonData.widgetDataList.map(d => d.dataList.pop().refName).pop()}));
          break;
        case 'SOUND_ON':
            this.sendEvent({name: 'custom', params: {type: 'sound_on'}}, of({}));
        break;
        case 'SOUND_OFF':
            this.sendEvent({name: 'custom', params: {type: 'sound_off'}}, of({}));
        break;
        case 'CONTR_PARAM_LOCKED_MODE_FEEDBACK':
          this.event_cdp$.emit({name: 'done', params: {type: 'locked'}, data: jsonData.payload});
          break;
        case 'CONTR_PARAM_MANUALLY_MODE_FEEDBACK':
          this.event_cdp$.emit({name: 'done', params: {type: 'manually'}, data: jsonData.payload});
        break;
        case 'CONTR_PARAM_SET_VALUE_FEEDBACK':
          this.event_cdp$.emit({name: 'done', params: {type: 'setvalue'}, data: jsonData.payload});
          break;
        case 'CONTR_SCHEDULE_LOCK_FEEDBACK':
          this.event_controller$.emit({name: 'done', params: {type: 'setvalue'}, data: jsonData.payload});
          break;
        case 'CTRL_HW_OS_FEEDBACK':
          this.event_controller_cmd$.emit({name: 'done', params: {type: 'setvalue'}, data: jsonData.payload});
          break;
        case 'CTRL_HW_PKG_INFO_FEEDBACK':
          this.event_controller_cmd$.emit({name: 'done', params: {type: 'setvalue'}, data: jsonData.payload});
          break;
        case 'CTRL_HW_RESET_FEEDBACK':
          this.event_controller_cmd$.emit({name: 'done', params: {type: 'setvalue'}, data: jsonData.payload});
          break;
        case 'CTRL_HW_PKG_UPGRADE_FEEDBACK':
          this.event_controller_cmd$.emit({name: 'done', params: {type: 'setvalue'}, data: jsonData.payload});
          break;
        case 'CTRL_HW_TUNNEL_FEEDBACK':
          this.event_controller_cmd$.emit({name: 'done', params: {type: 'setvalue'}, data: jsonData.payload});
          break;
        case 'CTRL_HW_GET_SN_FEEDBACK':
          this.event_controller_cmd$.emit({name: 'done', params: {type: 'setvalue'}, data: jsonData.payload});
          break;
        case 'CTRL_HW_GET_SIM_CCID_FEEDBACK':
          this.event_controller_cmd$.emit({name: 'done', params: {type: 'setvalue'}, data: jsonData.payload});
          break;
        default:
          break;
      }
    }

    if (jsonData.rowList) {
      jsonData.rowList.forEach(data => this.sendEvent({name: 'custom', params: {type: 'sound'}}, of({data})));
    }
    if (jsonData.widgetDataList) {
      jsonData.widgetDataList.forEach(data => this.parseWSData(data));
    }

    if (jsonData.dataList) {
      jsonData.dataList.forEach(command => this.parseWSData(command));
    }
  }

  async playSound(fileName): Promise<any> {
      this.audio.src = environment.mediaUrl + (environment.mediaUrl.substr(-1) === '/' ? '' : '/') + fileName;
      this.audio.load();
      return this.audio.play();
  }

  removeAudio() {
    this.audio.src = '';
  }
}
